"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Group, Loader2, Search } from "lucide-react"

import { mapStripeDataToProducts } from "@/lib/utils/product-mapper"
import { Input } from "@/components/ui/input"
import ContextualNavbar from "@/components/supervvisor/supervvisor-application-navbar"
import { CategoryDetails } from "@/components/supervvisor/supervvisor-category-details"
import ProductCard from "@/components/supervvisor/supervvisor-product-card"
import { ProductData } from "@/app/types/product"

interface Category {
  name: string
  key: string
  order: number
  description?: string
  image?: string
}

interface ProductHomeProps {
  session: {
    user?: {
      email?: string
      sub?: string
    } | null
  } | null
  supportLink?: string
}

export function ProductHome({ session, supportLink }: ProductHomeProps) {
  const [currentContext, setCurrentContext] = useState("all-products")
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  )
  const [products, setProducts] = useState<ProductData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [subscribeProducts, setSubscribeProducts] = useState<string[]>([])
  const [cancelSubProducts, setCancelSubProducts] = useState<string[]>([])
  const [paymentPendingSubProducts, setPaymentPendingSubProducts] = useState<
    string[]
  >([])
  const [menuItems, setMenuItems] = useState<Category[]>([
    { name: "All Products", key: "all-products", order: 0 },
  ])
  const [priceIdsMap, setPriceIdsMap] = useState<{ [key: string]: string }>({})
  const [subscriptionDetailsMap, setSubscriptionDetailsMap] = useState<{
    [key: string]: {
      subscription_renew_date: string | Date
      stripe_product_id: string
      stripe_price_id: string
      subscription_status: string
      cancel_at?: string | null
    }
  }>({})

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch("/api/stripe/products")
        const data = await response.json()
        const mappedProducts = mapStripeDataToProducts(data)
        setProducts(Array.isArray(mappedProducts) ? mappedProducts : [])
        if (session) {
          fetchSubscriptions()
        }
      } catch (error) {
        console.error("Error fetching products:", error)
        setProducts([])
      } finally {
        setIsLoading(false)
      }
    }
    const fetchSubscriptions = async () => {
      try {
        setIsLoading(true)
        const response = await fetch("/api/stripe/subscription")
        const subscriptions = await response.json()
        const productIdsSet = new Set()
        const cancelSubProductIdsSet = new Set()
        const paymentPendingSubProductIdsSet = new Set()
        const priceIdsSet = new Set()
        const subscriptionDetails: { [key: string]: any } = {}
        // Extract product IDs from subscription items
        subscriptions.forEach((subscription: any) => {
          productIdsSet.add(subscription.stripe_product_id)
          setPriceIdsMap((prev) => ({
            ...prev,
            [subscription.stripe_product_id]: subscription.stripe_price_id,
          }))
          subscriptionDetails[subscription.stripe_product_id] = subscription

          if (subscription.cancel_at && subscription.cancel_at !== null) {
            cancelSubProductIdsSet.add(subscription.stripe_product_id)
          }
          if (
            subscription.subscription_status === "past_due" ||
            subscription.subscription_status === "incomplete"
          ) {
            paymentPendingSubProductIdsSet.add(subscription.stripe_product_id)
          }
        })
        const productIds = Array.from(productIdsSet) as string[] // Convert Set to Array and assert type
        setSubscribeProducts(productIds) // Set the filtered products
        setCancelSubProducts(Array.from(cancelSubProductIdsSet) as string[]) // Set the filtered products
        setPaymentPendingSubProducts(
          Array.from(paymentPendingSubProductIdsSet) as string[]
        )
        setSubscriptionDetailsMap(subscriptionDetails)
      } catch (error) {
        console.error("Error fetching products:", error)
        //setProducts([])
      } finally {
        setIsLoading(false)
      }
    }
    const fetchCategories = async () => {
      try {
        const response = await fetch("/api/categories", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })
        const categories = await response.json()
        // Combine static menu items with fetched categories
        const updatedMenuItems = [
          { name: "All Products", key: "all-products", order: 0 },
          ...categories.map((category: any) => ({
            name: category.name,
            key: category.slug,
            order: category.sort_order,
            image: category.image,
            description: category.description,
          })),
        ].sort((a, b) => a.order - b.order)
        setMenuItems(updatedMenuItems)
      } catch (error) {
        console.error("Error fetching categories:", error)
      }
    }

    fetchProducts()
    fetchCategories()
  }, [])
  const filteredProducts = products.filter((product) => {
    const categories = (product.metadata.category || "")
      .split(",")
      .map((cat) => cat.trim())

    if (
      currentContext !== "all-products" &&
      !categories.includes(currentContext)
    ) {
      return false
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      const price = product.prices?.[0]?.unit_amount ?? 0
      const name = product.name.toLowerCase()
      const marketingFeatures =
        product.marketing_features?.map((feature) =>
          feature.name.toLowerCase()
        ) || []

      return (
        categories.some((category) =>
          category.toLowerCase().includes(searchLower)
        ) ||
        name.includes(searchLower) ||
        marketingFeatures.some((feature) => feature.includes(searchLower))
      )
    }
    return true
  })

  // Sort products by is_featured metadata and filter active ones
  const activeProducts = filteredProducts.filter((product) => product.active)
  const handleContextChange = (itemName: string) => {
    setCurrentContext(itemName)
    setSearchTerm("")
    setSelectedCategory(menuItems.find((item) => item.key === itemName) || null)
    // Right now we fetch all products, but in the future we will fetch
    // products based on the category
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (currentContext !== "all-products") {
      setCurrentContext("all-products")
    }
    setSearchTerm(event.target.value)
  }

  return (
    <div className="mx-auto flex flex-1 flex-grow flex-col gap-4 lg:gap-6 xl:max-w-[1130px]">
      <div className="flex flex-wrap items-center justify-between">
        <div className="flex items-center justify-start">
          <h1>Products</h1>
        </div>
        <div className="flex items-center space-x-2 lg:space-x-4">
          {" "}
          {/* Added space-x-4 for spacing between items */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 cursor-pointer text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search Marketplace"
              className="w-0 bg-white pl-2 text-[#333333] opacity-0 transition-all duration-300 ease-in-out hover:w-[210px] hover:pl-8 hover:opacity-100 focus:w-[210px] focus:pl-8 focus:opacity-100 sm:hover:w-[210px] sm:focus:w-[210px] sm:md:w-[210px] md:w-[210px] md:pl-8 md:opacity-100"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>
      <div className="home-products-navbar">
        <ContextualNavbar
          items={menuItems}
          currentContext={currentContext}
          onItemClick={handleContextChange}
        />
      </div>
      <hr className="mb-0 h-[1px] lg:-mt-2" />
      {currentContext !== "all-products" && (
        <CategoryDetails
          name={selectedCategory?.name || ""}
          description={selectedCategory?.description || ""}
          imageUrl={selectedCategory?.image || ""}
        />
      )}
      <h4 className="hidden text-lg text-secondary">
        {menuItems.find((item) => item.key === currentContext)?.name ||
          currentContext}
      </h4>
      <div className="flex flex-1 items-center justify-center">
        {isLoading ? (
          <div className="flex items-center justify-center text-primary">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading products...</span>
          </div>
        ) : activeProducts.length > 0 ? (
          <div className="grid w-full grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:gap-[30px]">
            {activeProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                productUrl={
                  session
                    ? `/marketplace/${product.id}`
                    : `/products/${product.id}`
                }
                isCancelled={
                  cancelSubProducts.includes(product.id) ? true : false
                }
                isPaymentPending={
                  paymentPendingSubProducts.includes(product.id) ? true : false
                }
                selectedPriceId={priceIdsMap[product.id]}
                subscriptionDetails={
                  subscriptionDetailsMap[product.id]
                    ? subscriptionDetailsMap[product.id]
                    : undefined
                }
              />
            ))}
          </div>
        ) : (
          <div className="text-center">
            <Image
              src="/working-on-it.svg"
              alt="No products available"
              width={410}
              height={207}
              className="mx-auto mb-8 mt-4 opacity-50"
            />
            <h2 className="mb-2 text-[32px]">We&apos;re working on it!</h2>
            <p className="mx-auto max-w-[504px] text-lg">
              We&apos;re bringing more products to the marketplace soon. Check
              back later, or{" "}
              <Link
                href={`mailto:${supportLink}`}
                className="text-gray-800 underline hover:text-primary"
              >
                contact us
              </Link>{" "}
              to request a product.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
